import type { ClinicSize, PersonalizationStep } from '../../contracts';

/**
 * The two form fields the layout decision reads, typed structurally so the
 * app's `PersonalizationFormData` satisfies it without crossing the door.
 */
export type PreviewLayoutFormData = {
  clinicSize: ClinicSize | null;
  specialty: unknown;
};

export type PreviewLayout =
  | { kind: 'completion' }
  | { kind: 'document-selection' }
  | { kind: 'mic-permissions' }
  | { kind: 'mic-test' }
  | {
      kind: 'template-flow';
      innerStep: 'template-creation' | 'template-loading' | 'template-review';
    }
  | { kind: 'clinic-grid' }
  | { kind: 'invite-team' }
  | {
      kind: 'badge-card';
      showSpecialty: boolean;
      showClinic: boolean;
    };

/**
 * Maps onboarding step + form data to preview layout. Single source for PreviewPanel branching.
 */
export function resolvePreviewLayout(
  currentStep: PersonalizationStep,
  formData: PreviewLayoutFormData
): PreviewLayout | null {
  if (currentStep === 'completion') {
    return { kind: 'completion' };
  }
  if (currentStep === 'document-selection') {
    return { kind: 'document-selection' };
  }
  if (currentStep === 'mic-permissions') {
    return { kind: 'mic-permissions' };
  }
  if (
    currentStep === 'mic-test' ||
    currentStep === 'note-ready' ||
    currentStep === 'note-edit' ||
    // The guided-tour screens each drive the same live note preview, switching
    // its tabs — same surface as the accordion they replace.
    currentStep === 'tour-detail' ||
    currentStep === 'tour-artefacts' ||
    currentStep === 'tour-evidence'
  ) {
    return { kind: 'mic-test' };
  }
  if (
    currentStep === 'template-creation' ||
    currentStep === 'template-loading' ||
    currentStep === 'template-review'
  ) {
    return { kind: 'template-flow', innerStep: currentStep };
  }
  if (currentStep === 'invite-team' || currentStep === 'tour-invite-team') {
    return { kind: 'invite-team' };
  }
  if (
    currentStep === 'clinic' &&
    formData.clinicSize &&
    formData.clinicSize !== 'only-me'
  ) {
    return { kind: 'clinic-grid' };
  }
  if (currentStep === 'about-you') {
    return {
      kind: 'badge-card',
      showSpecialty: formData.specialty !== null,
      showClinic: false,
    };
  }
  if (currentStep === 'name' || currentStep === 'role' || currentStep === 'clinic') {
    return {
      kind: 'badge-card',
      showSpecialty: currentStep === 'role' || currentStep === 'clinic',
      showClinic: currentStep === 'clinic',
    };
  }
  return null;
}
